import { useEffect, useMemo, useState } from "react"
import { EventTicketsFragment } from "../../../types.graphql"
import { Field } from "@components/controls/Field"
import { SimpleSelect } from "@components/controls/SimpleSelect"
import { formatMoney } from "@utils/money"
import { QuantitySelector } from "@components/controls/QuantitySelector"
import { Button } from "@components/controls/Button"
import { IconCaretRight } from "@components/svg/Icons"
import { useCartController } from "@features/accounts/hooks/useCartController"
import { useRestorableState, useRoute, useRouter } from "eddev/routing"
import { twMerge } from "tailwind-merge"

type Props = {
  data: EventTicketsFragment
  eventId: number
}

export function TicketSelector(props: Props) {
  const route = useRoute()
  const router = useRouter()
  const [readyToProceed, setReadyToProceed] = useState(false)
  const cart = useCartController("event", {
    enabled: readyToProceed,
  })
  const tickets = props.data.eventInfo?.eventConfiguration?.tickets
  const [currentTicket, setCurrentTicket] = useState(tickets?.[0].id!)
  const [selection, setSelection] = useRestorableState<Record<number, number>>("event_" + props.data.id, {})

  const ticket = tickets?.find((t) => t.id === currentTicket)
  const prices = ticket?.prices?.filter((p) => props.data.ticketProducts?.some((tp) => tp?.priceId === p.id))

  const cartInfo = useMemo(() => {
    let total = 0
    let totalTickets = 0
    for (let key in selection) {
      const id = parseInt(key)
      const value = selection[id]
      const product = props.data.ticketProducts?.find((p) => p?.product?.databaseId == id)
      total += value * parseFloat(product?.product?.price?.replace(/(^\$|,)/g, "")! ?? "0")
      totalTickets += value
    }
    return { total, totalTickets }
  }, [cart, props.data.ticketProducts])

  useEffect(() => {
    if (cart.pending || !readyToProceed) return
    if (cart.data?.contents?.itemCount && cart.success) {
      const timer = setTimeout(() => {
        router.navigate("/event-checkout?event=" + encodeURIComponent(route.pathname) + "&id=" + props.eventId)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [cart.success, cart.data?.contents?.itemCount, cart.pending, readyToProceed])

  return (
    <div className="flex flex-col gap-4">
      {tickets?.length! > 1 && (
        <Field label="Select Ticket" grow>
          <SimpleSelect
            value={currentTicket}
            onChange={(e) => setCurrentTicket(e.currentTarget.value)}
            options={
              tickets?.map((t) => ({
                value: t.id!,
                label: t.label!,
              }))!
            }
          />
        </Field>
      )}
      {prices?.length! > 0 ? (
        prices!.map((price) => {
          const product = props.data.ticketProducts?.find((p) => p?.priceId === price.id)
          const soldOut = product?.product?.stockStatus === "OUT_OF_STOCK"
          return (
            <div key={price.id} className="flex flex-col gap-2">
              <div className={twMerge("type-title-xs-roman", soldOut && "text-grey-mid")}>
                {price.label} — {formatMoney(product?.product?.price! || 0)}
                {soldOut && " (Sold out)"}
              </div>
              <QuantitySelector
                value={selection[product?.product?.databaseId!] ?? 0}
                onValueChange={(value) => setSelection({ ...selection, [product?.product?.databaseId!]: value })}
                max={Math.min(product?.product?.stockQuantity ?? 20, price.limit ?? 20)}
                disabled={soldOut}
              />
            </div>
          )
        })
      ) : (
        <div className="type-title-xs-roman">No prices available</div>
      )}
      {cartInfo.totalTickets > 0 && (
        <div>
          <div className="flex justify-between">
            <div className="type-title-xs-medium">Total</div>
            <div className="type-title-xs-medium">{formatMoney(cartInfo.total)}</div>
          </div>
          <div className="pt-4">
            <Button
              onClick={() => {
                cart.setItems(
                  Object.entries(selection)
                    .map(([key, quantity]) => ({
                      productId: parseInt(key),
                      quantity: quantity,
                    }))
                    .filter((item) => item.quantity > 0)
                )
                setReadyToProceed(true)
              }}
              loading={cart.pending || readyToProceed}
              rightIcon={<IconCaretRight />}
            >
              {cartInfo.total > 0 ? "Purchase tickets" : "Register"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
